/**
 * SpawnAgentModal — start an agent from a role. Demo page: /ui-elements/dialogs; field from /form-elements.
 * POST /agents/instances/spawn needs CanManageAgents, so the page only mounts it inside that policy.
 */
import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Form, FormActions, SelectFormField, useZodForm } from '../../../components/form/index.js';
import { Callout, Modal, Skeleton } from '../../../components/ui/index.js';
import { describeQueryError } from '../../../components/ui/QueryBoundary.js';
import { listTemplatesOptions, spawnAgentMutation } from '../../../generated/api/@tanstack/react-query.gen.js';
import { zAgentSpawnRequest } from '../../../generated/api/zod.gen.js';

const spawnSchema = zAgentSpawnRequest
  .pick({ templateSlug: true })
  .extend({ templateSlug: zAgentSpawnRequest.shape.templateSlug.min(1) });

interface SpawnAgentModalProps {
  open: boolean;
  onClose: () => void;
  onSpawned: () => void;
}

export const SpawnAgentModal: React.FC<SpawnAgentModalProps> = ({ open, onClose, onSpawned }) => {
  const roles = useQuery({ ...listTemplatesOptions({ query: { includeDisabled: false } }), enabled: open });
  const spawn = useMutation(spawnAgentMutation());
  const form = useZodForm(spawnSchema, { defaultValues: { templateSlug: '' } });

  const options = (roles.data?.data ?? []).map((role) => ({
    value: role.slug,
    label: role.title,
    description: role.departmentId ?? undefined,
  }));

  return (
    <Modal open={open} onClose={onClose} title="Spawn agent" description="Start a new agent from one of your roles.">
      {roles.isPending ? (
        <Skeleton height={72} rounded="rounded-lg" />
      ) : roles.isError ? (
        <Callout tone="danger" title={describeQueryError(roles.error, 'the roles')} />
      ) : (
        <Form
          form={form}
          onSubmit={async (values) => {
            await spawn.mutateAsync({ body: values });
            form.reset();
            onSpawned();
          }}
        >
          <SelectFormField name="templateSlug" label="Role" required options={options} placeholder="Choose a role" />
          <FormActions submitLabel="Spawn agent" onCancel={onClose} />
        </Form>
      )}
    </Modal>
  );
};
